import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useModuleView } from '../../hooks/useModuleView'
import ModuleOverviewCard from '../../components/Module/ModuleOverviewCard'
import ModuleViewBackLink from '../../components/Module/ModuleViewBackLink'
import FyMonthlyBarChart from '../../components/Charts/FyMonthlyBarChart'
import { currentFyLabel } from '../../utils/financialYearMonths'
import { 
  Users,
  Activity,
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Brain,
  DollarSign,
  Target,
  AlertTriangle,
  CheckCircle,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  MessageSquare,
  FileText,
  Briefcase,
  CreditCard,
  Receipt,
  Wallet,
  Banknote,
  Landmark,
  PiggyBank,
  Flame,
  HelpCircle
} from 'lucide-react'
import { accountsService } from '../../services/accountsService'
import founderKpiService from '../../services/founderKpiService'
import RevenueForecastPanel from '../../components/RevenueForecastPanel/RevenueForecastPanel'
import { toast } from 'react-toastify'
// Consolidated CSS is imported globally'

const getCurrentMonth = () => {
  const n = new Date()
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`
}

const Billing = () => {
  const navigate = useNavigate()
  const moduleView = useModuleView()
  const [showHelp, setShowHelp] = useState(false)
  const [helpTab, setHelpTab] = useState('masters')
  const [loading, setLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState(null)
  const [cashSummary, setCashSummary] = useState(null)
  const [founderKpis, setFounderKpis] = useState(null)
  const [founderKpisLoading, setFounderKpisLoading] = useState(false)
  const [founderKpiMonth, setFounderKpiMonth] = useState(getCurrentMonth())

  useEffect(() => {
    loadDashboardData()
    if (moduleView === 'overview') {
      loadCashSummary()
      loadFounderKpis()
    }
  }, [moduleView, founderKpiMonth])

  const loadFounderKpis = async () => {
    try {
      setFounderKpisLoading(true)
      const res = await founderKpiService.getFounderKpis(founderKpiMonth)
      if (res?.success) setFounderKpis(res.data)
    } catch (e) {
      console.error('Founder KPIs:', e)
    } finally {
      setFounderKpisLoading(false)
    }
  }

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const response = await accountsService.getDashboardStats()
      if (response && response.success) {
        setDashboardData(response.data)
      } else {
        toast.error('Failed to load accounts dashboard data')
      }
    } catch (error) {
      console.error('Error loading accounts dashboard:', error)
      toast.error('Failed to load accounts dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const loadCashSummary = async () => {
    try {
      const response = await accountsService.getCashSummary()
      if (response && response.success) {
        setCashSummary(response.data)
      }
    } catch (error) {
      console.error('Error loading cash summary:', error)
    }
  }

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0)
  }

  // Format time ago
  const formatTimeAgo = (date) => {
    if (!date) return 'Just now'
    const now = new Date()
    const past = new Date(date)
    const diffInSeconds = Math.floor((now - past) / 1000)
    
    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`
    return past.toLocaleDateString()
  }

  // Get user initials
  const getUserInitials = (name) => {
    if (!name) return 'U'
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  const mastersHelpContent = (
    <>
      <h3>Accounts Masters – Overview</h3>
      <p>Masters define the foundational data used for financial transactions, invoices, payments, and accounting. Configure these before recording payments or running reports.</p>

      <h4>Customers</h4>
      <p><strong>Purpose:</strong> Maintain customer account profiles, contact details, and payment terms for billing and receivables.</p>
      <p><strong>Details:</strong> The Customers master (in Sales) holds customer names, addresses, payment terms, credit limits, and status. Customers are used in invoices, quotations, and payment allocation. Open via the Customers card to manage the list and create or edit customer records.</p>

      <h4>Products</h4>
      <p><strong>Purpose:</strong> Maintain the billable products and service catalog used on invoices and revenue recognition.</p>
      <p><strong>Details:</strong> The Products/Services catalog (in Procurement or linked from Sales) defines items you sell or bill for: code, name, description, unit, and price. Use it so invoices and reports reference a consistent product list. Open the Products card to manage the catalog.</p>

      <h4>Payment Terms</h4>
      <p><strong>Purpose:</strong> Define payment terms and billing cycles (e.g. Net 30, Net 60, advance, milestone-based).</p>
      <p><strong>Details:</strong> The Payment Terms master (in Sales) stores terms such as due days, discount rules, and labels. These are applied to customers and invoices to calculate due dates and track ageing. Open Payment Terms to add or edit terms used across Accounts and Sales.</p>

      <h4>Tax Codes</h4>
      <p><strong>Purpose:</strong> Configure tax rates and compliance codes for invoices and reporting.</p>
      <p><strong>Details:</strong> The Tax Codes master (SAC / HSN or similar) holds tax rates, codes, and rules. Invoices and payments use these for tax calculation and compliance. Open Tax Codes to maintain the codes used in financial transactions and reports.</p>

      <h4>Cash Account Master</h4>
      <p><strong>Purpose:</strong> Set up cash and bank accounts for tracking balances and transactions.</p>
      <p><strong>Details:</strong> The Cash Account Master lists all cash and bank accounts (e.g. current account, petty cash). Payments and cash transactions are posted to these accounts. Use it to add accounts, set opening balances, and keep account details in one place. Required for Payments and Cash Transactions.</p>

      <h3>How to use</h3>
      <p>Click any card to open that master’s screen. Customers, Payment Terms, and Tax Codes open in Sales; Products may open in Procurement; Cash Account Master opens in Accounts. Use <strong>New</strong> or <strong>Create</strong> to add records. Set up these masters before recording payments or running invoice and cash reports.</p>
    </>
  )

  const transactionsHelpContent = (
    <>
      <h3>Accounts Transactions – Overview</h3>
      <p>Transactions cover received payments, cash and bank movements, refunds, and credit notes. Use these after Accounts Masters (especially Customers and Cash Account Master) are configured.</p>

      <h4>Payments</h4>
      <p><strong>Purpose:</strong> Record and manage payments received from customers against invoices or on account.</p>
      <p><strong>Details:</strong> The Payments screen lists and creates payment entries. For each payment you typically capture amount, date, payment method, customer, and allocation to one or more invoices (or leave as advance/on-account). Receipts can be generated or tracked. Use the list to filter by date, customer, or status and to reconcile with bank statements.</p>

      <h4>Cash Transactions</h4>
      <p><strong>Purpose:</strong> Record debits and credits for cash and bank accounts (e.g. deposits, withdrawals, transfers, adjustments).</p>
      <p><strong>Details:</strong> Cash Transactions lets you post entries to the accounts defined in Cash Account Master. Each entry has an account, date, amount, type (debit/credit), reference, and optional description. Use it for bank feeds, manual entries, and internal transfers. Balances by account support reconciliation and cash flow reporting.</p>

      <h4>Refunds</h4>
      <p><strong>Purpose:</strong> Process and track refunds made to customers and related adjustments.</p>
      <p><strong>Details:</strong> The Refunds module records money returned to customers—e.g. after returns, overpayments, or disputes. Each refund is linked to a customer and optionally to an invoice or payment. Track status (Pending, Processed, Paid) and the cash/bank account used. Refunds reduce receivables and update cash balances when processed.</p>

      <h4>Credit Notes</h4>
      <p><strong>Purpose:</strong> Issue and manage credit notes and accounting adjustments that reduce what the customer owes.</p>
      <p><strong>Details:</strong> Credit Notes are formal documents that decrease the customer’s outstanding balance (e.g. for returns, pricing corrections, or goodwill). Create credit notes with line items and amounts; they can be applied against invoices or future invoices. Use the list to view issued credit notes and their application status. They feed ageing and outstanding reports.</p>

      <h3>How to use</h3>
      <p>Click <strong>Payments</strong> or <strong>Cash Transactions</strong> to open their list and create new entries. Allocate payments to invoices where applicable. Use <strong>Cash Transactions</strong> for all cash and bank movements. <strong>Refunds</strong> and <strong>Credit Notes</strong> handle customer refunds and balance reductions; ensure customers and cash accounts are set up in Masters first.</p>
    </>
  )

  const getHelpTitle = () => helpTab === 'transactions' ? 'Accounts Transactions – Help' : 'Accounts Masters – Help'
  const getHelpContent = () => helpTab === 'transactions' ? transactionsHelpContent : mastersHelpContent

  return (
    <div className="sales-dashboard">
      {/* Page Header */}
      <div className="page-header" style={{textAlign: 'left'}}>
        <div className="page-title" style={{textAlign: 'left', alignItems: 'flex-start'}}>
          <h1 style={{textAlign: 'left', margin: '0 0 4px 0'}}>Accounts</h1>
          <p style={{textAlign: 'left', margin: 0}}>Financial transactions, invoices, payments, and accounting reports</p>
        </div>
      </div>

      {/* Tab Content */}
      {moduleView === 'overview' && (
        <div className="tab-content">
          <ModuleViewBackLink onClick={() => navigate('/accounts')} />
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div>Loading accounts dashboard...</div>
            </div>
          ) : dashboardData ? (
            <>
              {/* Key Metrics Grid */}
              <div className="dashboard-metrics-grid">
                {/* Accounts Metrics */}
                <div className="metric-card revenue">
                  <div className="metric-header">
                    <div className="metric-icon">
                      <DollarSign size={16} />
                    </div>
                    <div className={`metric-trend ${dashboardData.metrics?.monthlyRevenue?.changeType || 'positive'}`}>
                      {dashboardData.metrics?.monthlyRevenue?.change >= 0 ? (
                        <TrendingUp size={12} />
                      ) : (
                        <TrendingDown size={12} />
                      )}
                      <span>
                        {dashboardData.metrics?.monthlyRevenue?.change >= 0 ? '+' : ''}
                        {dashboardData.metrics?.monthlyRevenue?.change?.toFixed(1) || '0'}%
                      </span>
                    </div>
                  </div>
                  <div className="metric-value">{formatCurrency(dashboardData.metrics?.monthlyRevenue?.value || 0)}</div>
                  <div className="metric-title">Monthly Revenue</div>
                  <div className="metric-subtitle">vs last month</div>
                </div>

                <div className="metric-card customers">
                  <div className="metric-header">
                    <div className="metric-icon">
                      <Receipt size={16} />
                    </div>
                    <div className={`metric-trend ${dashboardData.metrics?.invoicesGenerated?.changeType || 'positive'}`}>
                      {dashboardData.metrics?.invoicesGenerated?.change >= 0 ? (
                        <TrendingUp size={12} />
                      ) : (
                        <TrendingDown size={12} />
                      )}
                      <span>
                        {dashboardData.metrics?.invoicesGenerated?.change >= 0 ? '+' : ''}
                        {dashboardData.metrics?.invoicesGenerated?.change || 0}
                      </span>
                    </div>
                  </div>
                  <div className="metric-value">{dashboardData.metrics?.invoicesGenerated?.value || 0}</div>
                  <div className="metric-title">Invoices Generated</div>
                  <div className="metric-subtitle">This month</div>
                </div>

                <div className="metric-card churn">
                  <div className="metric-header">
                    <div className="metric-icon">
                      <AlertTriangle size={16} />
                    </div>
                    <div className={`metric-trend ${dashboardData.metrics?.overduePayments?.changeType || 'negative'}`}>
                      <TrendingDown size={12} />
                      <span>{formatCurrency(dashboardData.metrics?.overduePayments?.amount || 0)}</span>
                    </div>
                  </div>
                  <div className="metric-value">{dashboardData.metrics?.overduePayments?.count || 0}</div>
                  <div className="metric-title">Overdue Payments</div>
                  <div className="metric-subtitle">Requires follow-up</div>
                </div>

                <div className="metric-card burn">
                  <div className="metric-header">
                    <div className="metric-icon">
                      <CreditCard size={16} />
                    </div>
                    <div className="metric-trend neutral">
                      <CheckCircle size={12} />
                      <span>{dashboardData.metrics?.collectionRate?.status || 'Good'}</span>
                    </div>
                  </div>
                  <div className="metric-value">{dashboardData.metrics?.collectionRate?.value?.toFixed(1) || '0'}%</div>
                  <div className="metric-title">Collection Rate</div>
                  <div className="metric-subtitle">Payment efficiency</div>
                </div>
              </div>

              {/* Cash & Bank Summary Cards */}
              <div className="section-header" style={{ marginTop: '24px', marginBottom: '12px' }}>
                <h3>Cash & Bank Summary</h3>
              </div>
              <div className="dashboard-metrics-grid">
                <div className="metric-card revenue">
                  <div className="metric-header">
                    <div className="metric-icon">
                      <PiggyBank size={16} />
                    </div>
                  </div>
                  <div className="metric-value">{formatCurrency(cashSummary?.currentCashBalance ?? 0)}</div>
                  <div className="metric-title">Total Cash</div>
                  <div className="metric-subtitle">Cash accounts balance</div>
                </div>
                <div className="metric-card customers">
                  <div className="metric-header">
                    <div className="metric-icon">
                      <Landmark size={16} />
                    </div>
                  </div>
                  <div className="metric-value">{formatCurrency(cashSummary?.bankBalance ?? 0)}</div>
                  <div className="metric-title">Total Bank</div>
                  <div className="metric-subtitle">Bank accounts balance</div>
                </div>
                <div className="metric-card churn">
                  <div className="metric-header">
                    <div className="metric-icon">
                      <Wallet size={16} />
                    </div>
                  </div>
                  <div className="metric-value">{formatCurrency(cashSummary?.totalBalance ?? 0)}</div>
                  <div className="metric-title">Total Balance</div>
                  <div className="metric-subtitle">Cash + Bank</div>
                </div>
                <div className="metric-card burn">
                  <div className="metric-header">
                    <div className="metric-icon">
                      <TrendingUp size={16} />
                    </div>
                    <div className={`metric-trend ${(cashSummary?.monthToDateNetFlow ?? 0) >= 0 ? 'positive' : 'negative'}`}>
                      {(cashSummary?.monthToDateNetFlow ?? 0) >= 0 ? (
                        <TrendingUp size={12} />
                      ) : (
                        <TrendingDown size={12} />
                      )}
                      <span>{formatCurrency(Math.abs(cashSummary?.monthToDateNetFlow ?? 0))}</span>
                    </div>
                  </div>
                  <div className="metric-value">{formatCurrency(cashSummary?.monthToDateNetFlow ?? 0)}</div>
                  <div className="metric-title">MTD Net Flow</div>
                  <div className="metric-subtitle">Month to date</div>
                </div>
              </div>

              {/* Founder KPIs - Burn & Runway */}
              <div className="section-header d-flex justify-content-between align-items-center" style={{ marginTop: '24px', marginBottom: '12px' }}>
                <h3 className="mb-0">Founder KPIs</h3>
                <input type="month" className="form-control" style={{ width: 'auto' }} value={founderKpiMonth} onChange={(e) => setFounderKpiMonth(e.target.value)} />
              </div>
              <div className="dashboard-metrics-grid">
                <div className="metric-card churn">
                  <div className="metric-header">
                    <div className="metric-icon">
                      <Flame size={16} />
                    </div>
                  </div>
                  <div className="metric-value">
                    {founderKpisLoading ? '...' : formatCurrency(founderKpis?.monthlyBurn ?? 0)}
                  </div>
                  <div className="metric-title">Monthly Burn</div>
                  <div className="metric-subtitle">Approved expenses for the month</div>
                </div>
                <div className="metric-card burn">
                  <div className="metric-header">
                    <div className="metric-icon">
                      <Zap size={16} />
                    </div>
                    <div className={`metric-trend ${founderKpis?.runwayStatus === 'SAFE' ? 'positive' : founderKpis?.runwayStatus === 'WARNING' ? 'negative' : founderKpis?.runwayStatus === 'CRITICAL' ? 'negative' : 'neutral'}`}>
                      <span className={`badge ${founderKpis?.runwayStatus === 'SAFE' ? 'bg-success' : founderKpis?.runwayStatus === 'WARNING' ? 'bg-warning text-dark' : founderKpis?.runwayStatus === 'CRITICAL' ? 'bg-danger' : 'bg-secondary'}`}>
                        {founderKpis?.runwayStatus || '—'}
                      </span>
                    </div>
                  </div>
                  <div className="metric-value">
                    {founderKpisLoading ? '...' : founderKpis?.runwayMonths != null ? `${founderKpis.runwayMonths} months` : (founderKpis && !founderKpisLoading ? '—' : '...')}
                  </div>
                  <div className="metric-title">Runway</div>
                  <div className="metric-subtitle">at current burn rate</div>
                </div>
                <div className="metric-card revenue">
                  <div className="metric-header">
                    <div className="metric-icon">
                      <TrendingUp size={16} />
                    </div>
                    <div className={`metric-trend ${(founderKpis?.netFlowMTD ?? 0) >= 0 ? 'positive' : 'negative'}`}>
                      {(founderKpis?.netFlowMTD ?? 0) >= 0 ? (
                        <TrendingUp size={12} />
                      ) : (
                        <TrendingDown size={12} />
                      )}
                      <span>{formatCurrency(Math.abs(founderKpis?.netFlowMTD ?? 0))}</span>
                    </div>
                  </div>
                  <div className="metric-value">
                    {founderKpisLoading ? '...' : formatCurrency(founderKpis?.netFlowMTD ?? 0)}
                  </div>
                  <div className="metric-title">Net Flow MTD</div>
                  <div className="metric-subtitle">Credits minus debits this month</div>
                </div>
              </div>
              {founderKpis?.pendingApprovals > 0 && (
                <div style={{ marginTop: '8px', marginBottom: '4px', fontSize: '0.9rem', color: 'var(--color-text-muted, #8B93A7)' }}>
                  Pending approvals: <strong>{founderKpis.pendingApprovals}</strong> draft expense{founderKpis.pendingApprovals !== 1 ? 's' : ''} this month
                </div>
              )}

              {/* Revenue Forecast */}
              <div style={{ marginTop: '24px' }}>
                <RevenueForecastPanel month={founderKpiMonth} onMonthChange={setFounderKpiMonth} />
              </div>

              {/* Action Items & Alerts */}
              <div className="dashboard-action-section">
                <div className="dashboard-action-items">
                  <div className="section-header">
                    <h3>Accounts Action Items</h3>
                    <span className="item-count">{dashboardData.actionItems?.length || 0} items</span>
                  </div>
                  <div className="action-list">
                    {dashboardData.actionItems && dashboardData.actionItems.length > 0 ? (
                      dashboardData.actionItems.slice(0, 6).map((item, index) => (
                        <div key={index} className={`action-item ${item.priority || 'medium'}`}>
                          <div className="action-icon">
                            <AlertTriangle size={16} />
                          </div>
                          <div className="action-content">
                            <div className="action-title">{item.title}</div>
                            <div className="action-description">{item.description}</div>
                          </div>
                          <div className="action-time">{formatTimeAgo(item.timestamp)}</div>
                        </div>
                      ))
                    ) : (
                      <div className="action-item">
                        <div className="action-content">
                          <div className="action-description">No action items at this time</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="dashboard-team-activity">
                  <div className="section-header">
                    <h3>Accounts Team Activity</h3>
                    <span className="activity-count">{dashboardData.teamActivity?.length || 0} updates</span>
                  </div>
                  <div className="activity-list">
                    {dashboardData.teamActivity && dashboardData.teamActivity.length > 0 ? (
                      dashboardData.teamActivity.slice(0, 10).map((activity, index) => (
                        <div key={index} className="activity-item">
                          <div className="activity-avatar">{activity.user?.initials || 'U'}</div>
                          <div className="activity-content">
                            <div className="activity-text">
                              <strong>{activity.user?.name || 'Unknown User'}</strong> {activity.action}
                            </div>
                            <div className="activity-time">{formatTimeAgo(activity.timestamp)}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="activity-item">
                        <div className="activity-content">
                          <div className="activity-description">No recent activity</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Charts Section */}
              <div className="dashboard-charts-section">
                <div className="dashboard-chart-card revenue-chart">
                  <div className="chart-header">
                    <h3>Revenue Trend</h3>
                    <p>{dashboardData?.financialYear ? `FY ${dashboardData.financialYear}` : `FY ${currentFyLabel()}`}</p>
                  </div>
                  <div className="chart-content">
                    <FyMonthlyBarChart
                      series={dashboardData?.revenueTrend}
                      valueKey="revenue"
                      formatTooltip={(v) => formatCurrency(v)}
                      emptyMessage="No revenue data available"
                    />
                  </div>
                </div>

                <div className="dashboard-chart-card customer-health">
                  <div className="chart-header">
                    <h3>Payment Status Distribution</h3>
                    <p>Current month</p>
                  </div>
                  <div className="chart-content">
                    <div className="health-distribution">
                      {dashboardData.paymentStatusDistribution ? (
                        <>
                          {(() => {
                            const total = Object.values(dashboardData.paymentStatusDistribution).reduce((a, b) => a + b, 0)
                            const paid = dashboardData.paymentStatusDistribution.Paid || 0
                            const pending = dashboardData.paymentStatusDistribution.Pending || 0
                            const overdue = dashboardData.paymentStatusDistribution.Overdue || 0
                            const disputed = dashboardData.paymentStatusDistribution.Disputed || 0
                            
                            return (
                              <>
                                <div className="health-segment excellent">
                                  <div className="segment-bar" style={{width: total > 0 ? `${(paid / total) * 100}%` : '0%'}}></div>
                                  <div className="segment-info">
                                    <span className="segment-label">Paid</span>
                                    <span className="segment-count">{paid}</span>
                                  </div>
                                </div>
                                <div className="health-segment good">
                                  <div className="segment-bar" style={{width: total > 0 ? `${(pending / total) * 100}%` : '0%'}}></div>
                                  <div className="segment-info">
                                    <span className="segment-label">Pending</span>
                                    <span className="segment-count">{pending}</span>
                                  </div>
                                </div>
                                <div className="health-segment at-risk">
                                  <div className="segment-bar" style={{width: total > 0 ? `${(overdue / total) * 100}%` : '0%'}}></div>
                                  <div className="segment-info">
                                    <span className="segment-label">Overdue</span>
                                    <span className="segment-count">{overdue}</span>
                                  </div>
                                </div>
                                <div className="health-segment churned">
                                  <div className="segment-bar" style={{width: total > 0 ? `${(disputed / total) * 100}%` : '0%'}}></div>
                                  <div className="segment-info">
                                    <span className="segment-label">Disputed</span>
                                    <span className="segment-count">{disputed}</span>
                                  </div>
                                </div>
                              </>
                            )
                          })()}
                        </>
                      ) : (
                        <div style={{ padding: '20px', textAlign: 'center' }}>No payment data available</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div>No data available</div>
            </div>
          )}
        </div>
      )}

      {moduleView === 'masters' && (
        <div className="tab-content">
          <div className="billing-masters-content">
            <div className="section-header d-flex justify-content-between align-items-start flex-wrap gap-2" style={{ marginBottom: '16px' }}>
              <h3 className="mb-0">Accounts Masters</h3>
              <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => { setHelpTab('masters'); setShowHelp(true); }} title="Help">
                <HelpCircle size={18} />
                <span className="d-none d-sm-inline">Help</span>
              </button>
            </div>
            <div className="billing-masters-grid">
              <div 
                className="billing-master-card clickable" 
                onClick={() => navigate('/sales/customer-master')}
                style={{ cursor: 'pointer' }}
              >
                <div className="billing-master-icon">
                  <Users size={24} />
                </div>
                <div className="billing-master-content">
                  <h3>Customers</h3>
                  <p>Customer accounts profiles and terms</p>
                  <div className="billing-master-count">
                    {loading ? '…' : `${dashboardData?.masters?.customers ?? 0} Customers`}
                  </div>
                </div>
              </div>
              <div 
                className="billing-master-card clickable" 
                onClick={() => navigate('/procurement/product-master')}
                style={{ cursor: 'pointer' }}
              >
                <div className="billing-master-icon">
                  <Target size={24} />
                </div>
                <div className="billing-master-content">
                  <h3>Products</h3>
                  <p>Billable products and service catalog</p>
                  <div className="billing-master-count">
                    {loading ? '…' : `${dashboardData?.masters?.services ?? 0} Services`}
                  </div>
                </div>
              </div>
              <div 
                className="billing-master-card clickable" 
                onClick={() => navigate('/sales/payment-terms-master')}
                style={{ cursor: 'pointer' }}
              >
                <div className="billing-master-icon">
                  <CreditCard size={24} />
                </div>
                <div className="billing-master-content">
                  <h3>Payment Terms</h3>
                  <p>Payment terms and billing cycles</p>
                  <div className="billing-master-count">
                    {loading ? '…' : `${dashboardData?.masters?.paymentTerms ?? 0} Terms`}
                  </div>
                </div>
              </div>
              <div 
                className="billing-master-card clickable" 
                onClick={() => navigate('/sales/sac-master')}
                style={{ cursor: 'pointer' }}
              >
                <div className="billing-master-icon">
                  <Receipt size={24} />
                </div>
                <div className="billing-master-content">
                  <h3>Tax Codes</h3>
                  <p>Tax rates and compliance codes</p>
                  <div className="billing-master-count">
                    {loading ? '…' : `${dashboardData?.masters?.taxCodes ?? 0} Codes`}
                  </div>
                </div>
              </div>
              <div 
                className="billing-master-card clickable" 
                onClick={() => navigate('/accounts/cash-accounts')}
                style={{ cursor: 'pointer' }}
              >
                <div className="billing-master-icon">
                  <Landmark size={24} />
                </div>
                <div className="billing-master-content">
                  <h3>Cash Account Master</h3>
                  <p>Cash and bank accounts for tracking</p>
                  <div className="billing-master-count">Cash & Bank</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'transactions' && (
        <div className="tab-content">
          <div className="billing-transactions-content">
            <div className="section-header d-flex justify-content-between align-items-start flex-wrap gap-2" style={{ marginBottom: '16px' }}>
              <h3 className="mb-0">Accounts Transactions</h3>
              <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => { setHelpTab('transactions'); setShowHelp(true); }} title="Help">
                <HelpCircle size={18} />
                <span className="d-none d-sm-inline">Help</span>
              </button>
            </div>
            <div className="billing-transactions-grid">
              <ModuleOverviewCard
                prefix="billing-transaction"
                moduleName="Accounts"
                showArrow={false}
                onClick={() => navigate('/accounts?view=overview')}
              />
              <div
                className="billing-transaction-card clickable"
                onClick={() => navigate('/sales/tax-invoice')}
                style={{ cursor: 'pointer' }}
              >
                <div className="billing-transaction-icon">
                  <Receipt size={24} />
                </div>
                <div className="billing-transaction-content">
                  <h3>Tax Invoices</h3>
                  <p>Generated tax invoices and billing documents</p>
                  <div className="billing-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactions?.invoices ?? 0} Invoices`}
                  </div>
                </div>
              </div>
              <div
                className="billing-transaction-card clickable"
                onClick={() => navigate('/sales/proforma-invoice')}
                style={{ cursor: 'pointer' }}
              >
                <div className="billing-transaction-icon">
                  <FileText size={24} />
                </div>
                <div className="billing-transaction-content">
                  <h3>Proforma Invoices</h3>
                  <p>Preliminary invoices pending conversion</p>
                  <div className="billing-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactions?.proformaInvoices ?? 0} Proforma`}
                  </div>
                </div>
              </div>
              <div 
                className="billing-transaction-card clickable" 
                onClick={() => navigate('/accounts/payments')}
                style={{ cursor: 'pointer' }}
              >
                <div className="billing-transaction-icon">
                  <CreditCard size={24} />
                </div>
                <div className="billing-transaction-content">
                  <h3>Payments</h3>
                  <p>Received payments and receipts</p>
                  <div className="billing-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactions?.payments ?? 0} Received`}
                  </div>
                </div>
              </div>
              <div 
                className="billing-transaction-card clickable" 
                onClick={() => navigate('/accounts/cash-transactions')}
                style={{ cursor: 'pointer' }}
              >
                <div className="billing-transaction-icon">
                  <PiggyBank size={24} />
                </div>
                <div className="billing-transaction-content">
                  <h3>Cash Transactions</h3>
                  <p>Debits and credits for cash & bank accounts</p>
                  <div className="billing-transaction-count">Track Cash</div>
                </div>
              </div>
              <div className="billing-transaction-card">
                <div className="billing-transaction-icon">
                  <Wallet size={24} />
                </div>
                <div className="billing-transaction-content">
                  <h3>Refunds</h3>
                  <p>Processed refunds and adjustments</p>
                  <div className="billing-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactions?.refunds ?? 0} Processed`}
                  </div>
                </div>
              </div>
              <div className="billing-transaction-card">
                <div className="billing-transaction-icon">
                  <Banknote size={24} />
                </div>
                <div className="billing-transaction-content">
                  <h3>Credit Notes</h3>
                  <p>Credit notes and accounting adjustments</p>
                  <div className="billing-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactions?.creditNotes ?? 0} Issued`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'reports' && (
        <div className="tab-content">
          <div className="billing-reports-content">
            <div className="billing-reports-header">
              <h2>Accounts Reports</h2>
              <p>Comprehensive financial analytics and accounting reports</p>
            </div>
            <div className="billing-reports-grid">
              <div className="billing-report-card">
                <div className="billing-report-icon">
                  <BarChart3 size={24} />
                </div>
                <div className="billing-report-content">
                  <h3>Revenue Reports</h3>
                  <p>Revenue trends and financial analysis</p>
                  <div className="billing-report-status">Updated Today</div>
                </div>
              </div>
              <div className="billing-report-card">
                <div className="billing-report-icon">
                  <Users size={24} />
                </div>
                <div className="billing-report-content">
                  <h3>Customer Reports</h3>
                  <p>Customer accounts and payment analysis</p>
                  <div className="billing-report-status">Updated Today</div>
                </div>
              </div>
              <div className="billing-report-card">
                <div className="billing-report-icon">
                  <CreditCard size={24} />
                </div>
                <div className="billing-report-content">
                  <h3>Collection Reports</h3>
                  <p>Payment collection and aging analysis</p>
                  <div className="billing-report-status">Updated Today</div>
                </div>
              </div>
              <div className="billing-report-card">
                <div className="billing-report-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="billing-report-content">
                  <h3>Financial Reports</h3>
                  <p>Financial statements and tax reports</p>
                  <div className="billing-report-status">Updated Today</div>
                </div>
              </div>
              <div 
                className="billing-report-card clickable" 
                onClick={() => navigate('/accounts/reports/invoice-aging')}
                style={{ cursor: 'pointer' }}
              >
                <div className="billing-report-icon">
                  <Clock size={24} />
                </div>
                <div className="billing-report-content">
                  <h3>Invoice Aging</h3>
                  <p>Track outstanding invoices by aging buckets</p>
                  <div className="billing-report-status">Updated Today</div>
                </div>
              </div>
              <div 
                className="billing-report-card clickable" 
                onClick={() => navigate('/accounts/reports/sundry-debtors')}
                style={{ cursor: 'pointer' }}
              >
                <div className="billing-report-icon">
                  <Users size={24} />
                </div>
                <div className="billing-report-content">
                  <h3>Sundry Debtors</h3>
                  <p>Outstanding receivables from customers</p>
                  <div className="billing-report-status">Updated Today</div>
                </div>
              </div>
              <div 
                className="billing-report-card clickable" 
                onClick={() => navigate('/accounts/reports/outstanding-payments')}
                style={{ cursor: 'pointer' }}
              >
                <div className="billing-report-icon">
                  <DollarSign size={24} />
                </div>
                <div className="billing-report-content">
                  <h3>Outstanding Payments</h3>
                  <p>Project-based payment tracking by customer</p>
                  <div className="billing-report-status">Updated Today</div>
                </div>
              </div>
              <div 
                className="billing-report-card clickable" 
                onClick={() => navigate('/accounts/reports/founder-control')}
                style={{ cursor: 'pointer' }}
              >
                <div className="billing-report-icon">
                  <Target size={24} />
                </div>
                <div className="billing-report-content">
                  <h3>Founder Control</h3>
                  <p>Cash, burn, runway, pipeline & budget variance</p>
                  <div className="billing-report-status">Survival</div>
                </div>
              </div>
              <div 
                className="billing-report-card clickable" 
                onClick={() => navigate('/accounts/reports/burn-discipline')}
                style={{ cursor: 'pointer' }}
              >
                <div className="billing-report-icon">
                  <Flame size={24} />
                </div>
                <div className="billing-report-content">
                  <h3>Burn Discipline</h3>
                  <p>FY budget vs actual by category</p>
                  <div className="billing-report-status">Survival</div>
                </div>
              </div>
              <div 
                className="billing-report-card clickable" 
                onClick={() => navigate('/accounts/reports/runway-sensitivity')}
                style={{ cursor: 'pointer' }}
              >
                <div className="billing-report-icon">
                  <Zap size={24} />
                </div>
                <div className="billing-report-content">
                  <h3>Runway Sensitivity</h3>
                  <p>Scenario analysis: burn & pipeline</p>
                  <div className="billing-report-status">Survival</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title={getHelpTitle()}>
        {getHelpContent()}
      </HelpModal>
    </div>
  )
}

export default Billing
